import { useEffect, useMemo, useState } from 'react'
import { rgbToHex } from '@mui/material/styles'

import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Popover from '@mui/material/Popover'
import IconButton from '@mui/material/IconButton'

import { HexColorPicker } from 'react-colorful'
import ColorizeIcon from '@mui/icons-material/Colorize'
import { CheckOutlined } from '@mui/icons-material'

import { theme } from '@/context/ThemeContext'

const colorPalette = [
  '#F45D48',
  '#E91E63',
  '#9C27B0',
  '#673AB7',
  '#3F51B5',
  '#2196F3',
  '#03A9F4',
  '#00BCD4',
  '#009688',
  '#4CAF50',
  '#8BC34A',
  '#CDDC39',
  '#FFEB3B',
  '#FFC107',
  '#FF9800',
  '#FF5722',
]

const ColorPicker = ({ color, onChange, onCancel, onClose, currentColors = [] }) => {
  const [manualColor, setManualColor] = useState(color)
  const [selectedColor, setSelectedColor] = useState(color)
  const [anchorEl, setAnchorEl] = useState(null)
  const [themeColors, setThemeColors] = useState([selectedColor, ...currentColors])

  const handleColorChange = (color) => {
    setSelectedColor(color)
    setThemeColors((prevColors) => [color, ...prevColors.slice(1)])
    onChange(color)
  }

  const handlePickerOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePickerClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  const hexColor = useMemo(() => {
    if (manualColor.includes('rgb')) {
      return rgbToHex(manualColor)
    }
    return manualColor
  }, [manualColor])

  useEffect(() => {
    setManualColor(selectedColor)
  }, [selectedColor])
  const ColorBox = ({ color, selected }) => (
    <Box
      className="flex justify-center items-center"
      sx={{
        width: 30,
        height: 30,
        bgcolor: color,
        cursor: 'pointer',
        '&:hover': {
          border: '2px solid rgba(255, 255, 255, 0.5)',
        },
      }}
      onClick={() => handleColorChange(color)}
    >
      {selected && <CheckOutlined sx={{ color: theme.palette.getContrastText(color) }} />}
    </Box>
  )

  const handleManualChange = ({ target: { value } }) => {
    value = !value.includes("#") ? `#${value}` : value
    setManualColor(value)
    const isValidHexColor = /^#[0-9A-F]{6}$/i.test(value)
    if (isValidHexColor) {
      handleColorChange(value)
    }
  }

  return (
    <Paper sx={{
      pt: 2,
      bgcolor: '#1E1E1E',
      color: 'white',
      width: { xs: '100%', sm: '300px' },
      height: { xs: '95vh', sm: 'auto' },
    }}
      className='flex flex-col justify-between md:justify-start'
    >
      <Box className='px-4'>
        <Typography
          variant='h6'
          textAlign={'center'}
          sx={{
            color: '#7D8285',
          }}
          className='!mb-5'
        >
          COLOR
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TextField
            name='color-picker'
            value={hexColor}
            fullWidth
            onFocus={event => {
              event.target.select();
            }}
            onChange={handleManualChange}
            sx={{
              '& .MuiInputBase-input': {
                textAlign: 'center',
                textTransform: 'uppercase',
              },
            }}
            slotProps={{
              htmlInput: {
                maxLength: 7,
              },
              input: {
                startAdornment: (
                  <Typography
                    style={{
                      color: theme.palette.getContrastText(selectedColor),
                    }}
                  >
                    #
                  </Typography>
                ),

                style: {
                  background: selectedColor,
                  borderRadius: 4,
                  color: theme.palette.getContrastText(selectedColor),
                },
                endAdornment: (
                  <IconButton
                    onClick={handlePickerOpen}
                    sx={{
                      color: theme.palette.getContrastText(selectedColor),
                    }}
                  >
                    <ColorizeIcon />
                  </IconButton>
                ),
              },
            }}
          />
        </Box>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handlePickerClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box sx={{ p: 2 }}>
            <HexColorPicker
              color={selectedColor}
              onChange={(newColor) => {
                handleColorChange(newColor)
              }}
            />
          </Box>
        </Popover>
        <Typography
          variant='subtitle1'
          gutterBottom
        >
          THEME
        </Typography>
        <Box className='flex flex-wrap gap-2 mb-5'>
          {themeColors.map((color, index) => (
            <Box key={index}>
              <ColorBox color={color} selected={index === 0} />
            </Box>
          ))}
        </Box>
        <Typography
          variant='subtitle1'
          gutterBottom
        >
          COLORS
        </Typography>
        <Box className='flex flex-wrap gap-2'>
          {colorPalette.map((color) => (
            <Box key={color}>
              <ColorBox color={color} />
            </Box>
          ))}
        </Box>
      </Box>
      <Box className='px-4 md:px-0 mt-6 flex justify-between'>
        <Button
          className='grow !rounded-none'
          variant='outlined'
          color='secondary'
          onClick={() => {
            onClose()
          }}
        >
          <Typography sx={{ 
            color: `${selectedColor == color? "#E0E0E0": "#FFFFFF"}`,
            fontWeight: `${selectedColor == color? 400: 600}`,
          }}
          >
            APPLY
          </Typography>
        </Button>
        <Button
          className='grow !rounded-none'
          variant='outlined'
          color='secondary'
          onClick={() => {
            onCancel()
            onClose()
          }}
        >
          <Typography sx={{ 
            color: `${selectedColor == color? "#E0E0E0": "#FFFFFF"}`,
            fontWeight: `${selectedColor == color? 400: 600}`,
          }}
          >
            CANCEL
          </Typography>
        </Button>
      </Box>
    </Paper>
  )
}

export default ColorPicker
